import { createSessionScope } from '@scrambl/shared/session-scope'

interface CredentialSnapshot {
  revision: string | null
  data: Record<string, unknown>
}

export interface CredentialTransport {
  read(): Promise<CredentialSnapshot>
  write(
    expectedRevision: string | null,
    data: Record<string, unknown>,
  ): Promise<CredentialSnapshot>
}

/** Store-compatible adapter backed exclusively by the native OS credential vault. */
export class NativeCredentialStore {
  private snapshot: CredentialSnapshot = { revision: null, data: {} }
  constructor(private readonly transport: CredentialTransport) {}

  async reload(): Promise<void> {
    this.snapshot = await this.transport.read()
    desktopAccountSession.transition(
      typeof this.snapshot.data.auth_active_account_id === 'string'
        ? this.snapshot.data.auth_active_account_id
        : null,
    )
  }
  async get<T>(key: string): Promise<T | undefined> {
    return structuredClone(this.snapshot.data[key]) as T | undefined
  }
  async set(key: string, value: unknown): Promise<void> {
    this.snapshot.data[key] = structuredClone(value)
  }
  async delete(key: string): Promise<boolean> {
    const existed = key in this.snapshot.data
    delete this.snapshot.data[key]
    return existed
  }
  async save(): Promise<void> {
    // Native compare-and-swap refuses an old window's stale account snapshot.
    this.snapshot = await this.transport.write(
      this.snapshot.revision,
      structuredClone(this.snapshot.data),
    )
    desktopAccountSession.transition(
      typeof this.snapshot.data.auth_active_account_id === 'string'
        ? this.snapshot.data.auth_active_account_id
        : null,
    )
  }
}

export async function loadNativeCredentialStore(): Promise<NativeCredentialStore> {
  const { invoke } = await import('@tauri-apps/api/core')
  accountListenerReady ??= import('@tauri-apps/api/event')
    .then(async ({ listen }) => {
      await listen<string | null>('desktop-account-changed', event =>
        desktopAccountSession.transition(event.payload),
      )
    })
    .catch(error => {
      accountListenerReady = undefined
      throw error
    })
  await accountListenerReady
  const store = new NativeCredentialStore({
    read: () => invoke('credential_store_read'),
    write: (expectedRevision, data) =>
      invoke('credential_store_write', { expectedRevision, data }),
  })
  await store.reload()
  return store
}

export const desktopAccountSession = createSessionScope()
let accountListenerReady: Promise<void> | undefined
